import { Organization } from "@common/types/organization";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { ACTIVE_USER_ID_KEY } from "@constants";
import { safeLocalStorage, safeSessionStorage } from "@helpers/storage";
import { useRecordOrganizationClientAccess } from "./useRecordOrganizationClientAccess";
import {
    FC,
    lazy,
    PropsWithChildren,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { LazyChunkBoundary } from "@design-system/LazyChunkBoundary/LazyChunkBoundary";
// eslint-disable-next-line no-restricted-imports -- StateAware navigation primitives wrap react-router-dom directly
import { useLocation, Location, useNavigate } from "react-router-dom";

const WorkspaceEvictedModal = lazy(() =>
    import("@components/scope/WorkspaceEvictedModal").then((m) => ({
        default: m.WorkspaceEvictedModal,
    })),
);
import {
    ScopeContext,
    scopeProxy,
    getActiveWorkspaceKeyForUserId,
    getActiveOrganizationKeyForUserId,
    getCurrentWorkspaceTabKeyForUserId,
    getTabId,
    parseWorkspaceTabCell,
    InitializeScopeParams,
    WorkspaceTabCell,
} from "./scopeContext.lib";

export interface ScopeLocationState {
    organizationId?: string | null;
    workspaceId?: string | null;
}

const getUserId = () =>
    safeLocalStorage.getItem(ACTIVE_USER_ID_KEY) ?? "anonymous";

const getActiveWorkspaceKey = () => getActiveWorkspaceKeyForUserId(getUserId());

const getActiveOrganizationKey = () =>
    getActiveOrganizationKeyForUserId(getUserId());

const getCurrentWorkspaceTabKey = () =>
    getCurrentWorkspaceTabKeyForUserId(getUserId());

/**
 * Read a scope value: sessionStorage first (per-tab), fallback to localStorage
 * (last-used seed for new tabs that have never had a session scope set).
 */
const readScopeValue = (key: string): string | null =>
    safeSessionStorage.getItem(key) ?? safeLocalStorage.getItem(key);

export const ScopeProvider: FC<PropsWithChildren> = ({ children }) => {
    const location: Location<ScopeLocationState> = useLocation();
    const locationRef = useRef(location);
    locationRef.current = location;
    const navigate = useNavigate();

    const [locationState, setLocationState] = useState<ScopeLocationState>(
        () => {
            if (!safeLocalStorage.getItem(ACTIVE_USER_ID_KEY)) {
                return { organizationId: null, workspaceId: null };
            }

            return {
                organizationId:
                    location.state?.organizationId ??
                    readScopeValue(getActiveOrganizationKey()),
                workspaceId:
                    location.state?.workspaceId ??
                    readScopeValue(getActiveWorkspaceKey()),
            };
        },
    );

    const [isEvicted, setIsEvicted] = useState(false);

    // Holds the `source` of a pending scope change so the record effect can
    // attribute the resulting audit row. Consumed and cleared by the effect.
    const pendingClientAccessSourceRef =
        useRef<OrganizationMemberClientAccessSource | null>(null);

    useRecordOrganizationClientAccess({
        organizationId: locationState.organizationId,
        workspaceId: locationState.workspaceId,
        pendingSourceRef: pendingClientAccessSourceRef,
    });

    const persistState = useCallback((state: ScopeLocationState) => {
        if (!safeLocalStorage.getItem(ACTIVE_USER_ID_KEY)) {
            return;
        }

        if (state.organizationId) {
            safeSessionStorage.setItem(
                getActiveOrganizationKey(),
                state.organizationId,
            );
            safeLocalStorage.setItem(
                getActiveOrganizationKey(),
                state.organizationId,
            );
        } else {
            safeSessionStorage.removeItem(getActiveOrganizationKey());
            safeLocalStorage.removeItem(getActiveOrganizationKey());
        }

        if (state.workspaceId) {
            safeSessionStorage.setItem(
                getActiveWorkspaceKey(),
                state.workspaceId,
            );
            safeLocalStorage.setItem(
                getActiveWorkspaceKey(),
                state.workspaceId,
            );

            // Update the coordination cell so other tabs can detect a workspace
            // change and enforce the single-workspace-tab invariant.
            const cell: WorkspaceTabCell = {
                workspaceId: state.workspaceId,
                tabId: getTabId(),
                at: new Date().toISOString(),
            };
            safeLocalStorage.setItem(
                getCurrentWorkspaceTabKey(),
                JSON.stringify(cell),
            );
        } else {
            safeSessionStorage.removeItem(getActiveWorkspaceKey());
            safeLocalStorage.removeItem(getActiveWorkspaceKey());
        }
    }, []);

    // Listen for cross-tab workspace changes and evict this tab if another
    // tab has taken over a different workspace.
    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== getCurrentWorkspaceTabKey()) {
                return;
            }
            if (!event.newValue) {
                return;
            }

            let parsed: unknown;
            try {
                parsed = JSON.parse(event.newValue);
            } catch {
                return;
            }
            const cell = parseWorkspaceTabCell(parsed);
            if (!cell) {
                return;
            }

            // Ignore events we wrote ourselves.
            if (cell.tabId === getTabId()) {
                return;
            }

            // Only evict if we currently hold a workspace scope AND it differs
            // from the one the other tab just claimed.
            const ourWorkspaceId = safeSessionStorage.getItem(
                getActiveWorkspaceKey(),
            );
            if (!ourWorkspaceId) {
                return;
            }
            if (ourWorkspaceId === cell.workspaceId) {
                return;
            }

            setIsEvicted(true);
        };

        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    const value = useMemo(() => {
        const currentOrganizationId = locationState.organizationId;
        const currentWorkspaceId = locationState.workspaceId;

        const changeScope = ({
            organizationId,
            workspaceId,
        }: ScopeLocationState) => {
            persistState({ organizationId, workspaceId });

            setLocationState({ organizationId, workspaceId });

            navigate(
                { pathname: ".", search: locationRef.current.search },
                {
                    state: {
                        ...locationState,
                        organizationId,
                        workspaceId,
                    },
                },
            );
        };

        const clear = () => {
            changeScope({ organizationId: null, workspaceId: null });
        };

        return {
            organizationId: currentOrganizationId,
            workspaceId: currentWorkspaceId,
            activeWorkspaceKey: currentWorkspaceId ?? "anonymous",
            activeOrganizationKey: currentOrganizationId ?? "anonymous",
            changeActiveOrganization: (organization: Organization | null) => {
                changeScope({
                    organizationId: organization?.id,
                    workspaceId: null,
                });
            },
            changeActiveWorkspace: (
                workspaceId: string | null,
                source: OrganizationMemberClientAccessSource = OrganizationMemberClientAccessSource.OTHER,
            ) => {
                // Only stash the source when the workspace actually changes.
                // A same-workspace no-op does not re-run the record effect, so
                // setting the ref here would leave a stale source that a later
                // genuine change could consume and mis-attribute.
                if (workspaceId !== currentWorkspaceId) {
                    pendingClientAccessSourceRef.current = source;
                }
                changeScope({
                    organizationId: locationState.organizationId,
                    workspaceId,
                });
            },
            changeScope,
            initializeScope: (params: InitializeScopeParams) => {
                pendingClientAccessSourceRef.current =
                    params.source ??
                    OrganizationMemberClientAccessSource.HYDRATE;
                persistState({
                    organizationId: params.organizationId,
                    workspaceId: params.workspaceId,
                });
                setLocationState({
                    organizationId: params.organizationId,
                    workspaceId: params.workspaceId,
                });
            },
            clear,
        };
    }, [locationState, navigate, persistState]);

    useLayoutEffect(() => {
        scopeProxy.clear = value.clear;
    }, [value]);

    return (
        <ScopeContext.Provider value={value}>
            {children}
            {isEvicted && (
                <LazyChunkBoundary>
                    <WorkspaceEvictedModal
                        onReload={() => {
                            // Clear workspace from session so the reload lands in
                            // org scope rather than re-entering the workspace.
                            safeSessionStorage.removeItem(
                                getActiveWorkspaceKey(),
                            );
                            safeLocalStorage.removeItem(
                                getActiveWorkspaceKey(),
                            );
                            window.location.reload();
                        }}
                    />
                </LazyChunkBoundary>
            )}
        </ScopeContext.Provider>
    );
};
